import React, { Component, createRef } from "react";
import PropTypes from "prop-types";
import "./LoginPIN.css";
import { SERVER_URL, APP_FUNCTIONS_URL, LIBPANEL_DHC_ADDRESS } from "../../utils/constants";
const TIME_FOR_PIN_APPROVAL = 119; // seconds

class LoginPIN extends Component {
    constructor(props) {
        super(props);
        this.state = {
            pin: ["", "", "", "", "", ""],
            loading: false,
            error: "",
            pinEntered: false,
            timeRemaining: TIME_FOR_PIN_APPROVAL,
            intent: props.intent
        };

        // Create refs for each input field
        this.inputRefs = Array(6).fill().map(() => createRef());

        // For polling
        this.pollInterval = null;
        this.countdownInterval = null;
        this.pollTimeout = null;
    }

    componentDidMount() {
        // Focus the first input on component mount
        if (this.inputRefs[0].current) {
            this.inputRefs[0].current.focus();
        }

        // Expose clearPolling to parent component
        if (this.props.clearPollingRef) {
            this.props.clearPollingRef(this.clearPolling);
        }
    }

    componentWillUnmount() {
        this.clearPolling();
    }

    // Method to set error message - can be called by parent component
    setError = (message) => {
        this.setState({ error: message });
    }

    // Method to reset PIN state - can be called by parent component
    resetPinState = () => {
        this.setState({
            pin: ["", "", "", "", "", ""],
            pinEntered: false
        }, () => {
            // Re-focus the first input field after state update
            if (this.inputRefs[0].current) {
                this.inputRefs[0].current.focus();
            }
        });
    }

    // Method for handling "show reload" (timeout occurred)
    handleShowReload = (show) => {
        if (show) {
            // Time has expired, reset the PIN state
            this.resetPinState();
        }
    }

    // Setup polling for auth status - replaces useAuthPolling hook
    setupPolling = (sessionId) => {
        // Clear any existing polling
        this.clearPolling();

        // Start polling for auth status - USING THE CORRECT URL FROM YOUR CODE
        this.pollInterval = setInterval(async () => {
            try {
                // Use the correct endpoint from your original code
                const response = await fetch(`${SERVER_URL}/api/getDlockSessionStatus?sessionId=${sessionId}`);

                if (response.ok) {
                    const data = await response.json();

                    if (data.status === "APPROVED" || data.status === "REJECTED") {
                        // Found a definitive status, stop polling
                        this.clearPolling();

                        // Add sessionId to data
                        data.sessionId = sessionId;

                        // Pass result to parent
                        this.props.onAuthStatusChange(data);
                    }
                }
            } catch (error) {
                console.error("Error checking auth status:", error);
            }
        }, 3000);

        // Countdown timer for progress bar
        this.countdownInterval = setInterval(() => {
            this.setState(prevState => ({
                timeRemaining: Math.max(0, prevState.timeRemaining - 1)
            }));
        }, 1000);

        // Set timeout to show reload after validity period
        this.pollTimeout = setTimeout(() => {
            this.clearPolling();
            this.handleShowReload(true);
        }, TIME_FOR_PIN_APPROVAL * 1000);
    }

    // Method to clear all polling intervals and timeouts
    clearPolling = () => {
        if (this.pollInterval) {
            clearInterval(this.pollInterval);
            this.pollInterval = null;
        }

        if (this.countdownInterval) {
            clearInterval(this.countdownInterval);
            this.countdownInterval = null;
        }

        if (this.pollTimeout) {
            clearTimeout(this.pollTimeout);
            this.pollTimeout = null;
        }
    }

    handleChange = (e, index) => {
        const value = e.target.value;

        // Only allow digits
        if (!/^\d*$/.test(value)) return;

        // Update the PIN
        const newPin = [...this.state.pin];
        newPin[index] = value.slice(-1); // Only keep the last character

        this.setState({
            pin: newPin,
            error: "" // Clear any previous errors
        });

        // Move focus to next input if value is entered
        if (value !== "" && index < 5 && this.inputRefs[index + 1].current) {
            this.inputRefs[index + 1].current.focus();
        }

        // Submit when the last digit is entered
        if (index === 5 && value !== "") {
            this.handleSubmit(newPin.join(""));
        }
    }

    handleKeyDown = (e, index) => {
        // Handle backspace - move to previous input and clear it
        if (e.key === "Backspace" && index > 0 && this.state.pin[index] === "") {
            const newPin = [...this.state.pin];
            newPin[index - 1] = "";

            this.setState({ pin: newPin }, () => {
                if (this.inputRefs[index - 1].current) {
                    this.inputRefs[index - 1].current.focus();
                }
            });
        }
    }

    handlePaste = (e) => {
        // Prevent default paste behavior
        e.preventDefault();

        // Get pasted text
        const pastedText = e.clipboardData.getData('text');

        // Check if pasted text contains only digits
        if (!/^\d+$/.test(pastedText)) {
            this.setState({ error: "Dozwolone tylko cyfry" });
            return;
        }

        // Use up to 6 digits from the pasted text
        const digits = pastedText.slice(0, 6).split('');

        // Create a new PIN array with pasted digits
        const newPin = [...this.state.pin];
        digits.forEach((digit, index) => {
            if (index < 6) {
                newPin[index] = digit;
            }
        });

        // Update state with new PIN
        this.setState({ pin: newPin, error: "" }, () => {
            // Focus the next empty input or the last input if all filled
            const nextEmptyIndex = newPin.findIndex(digit => digit === '');

            if (nextEmptyIndex !== -1 && this.inputRefs[nextEmptyIndex].current) {
                this.inputRefs[nextEmptyIndex].current.focus();
            } else if (this.inputRefs[5].current) {
                // If all digits filled, focus the last input and submit
                this.inputRefs[5].current.focus();
                this.handleSubmit(newPin.join(''));
            }
        });
    }

    handleSubmit = async (pinCode) => {
        this.setState({
            loading: true,
            error: "",
            pinEntered: true,
            timeRemaining: TIME_FOR_PIN_APPROVAL
        });

        try {
            const response = await fetch(`${SERVER_URL}/api/postDlockPINEntered`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    "PIN": pinCode,
                    "partnerDHCAddress": LIBPANEL_DHC_ADDRESS,
                    "intent": this.state.intent,
                    "webHookUrl": `${APP_FUNCTIONS_URL}/dLockAuthWebhook`,
                }),
            });

            const data = await response.json();
            const newSessionId = data.sessionId;

            if (response.ok && newSessionId) {
                // Start polling for authentication status
                this.setupPolling(newSessionId);
            } else {
                // Handle PIN verification failure
                this.setState({
                    error: data.message || "Nieprawidłowy PIN.",
                    loading: false
                });
                // Clear the PIN and focus the first input
                this.resetPinState();
            }
        } catch (error) {
            console.error("Error verifying PIN:", error);
            this.setState({
                error: "Błąd podczas weryfikacji.",
                loading: false
            });
            // Clear the PIN and focus the first input
            this.resetPinState();
        }
    }

    render() {
        const { pin, loading, error, pinEntered, timeRemaining } = this.state;
        const progressPercent = (timeRemaining / TIME_FOR_PIN_APPROVAL) * 100;

        return (
            <div className="login-pin-container"
                style={{
                    display: "flex",
                    justifyContent: "center",
                }}>
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        padding: "1rem"
                    }}
                >
                    <div className="login-pin-fields">
                        {pin.map((digit, index) => (
                            <input
                                key={index}
                                ref={this.inputRefs[index]}
                                type="text"
                                inputMode="numeric"
                                maxLength={1}
                                value={digit}
                                onChange={(e) => this.handleChange(e, index)}
                                onKeyDown={(e) => this.handleKeyDown(e, index)}
                                onPaste={index === 0 ? this.handlePaste : null}
                                disabled={loading}
                                className="pin-digit-input"
                                autoComplete="off"
                            />
                        ))}
                    </div>

                    {/* Progress Bar */}
                    {pinEntered && (
                        <div style={{
                            width: "12vw",
                            position: "relative",
                            margin: "1em",
                            alignSelf: "center"
                        }}>
                            <div style={{
                                height: "4px",
                                backgroundColor: "#E0E0E0",
                                borderRadius: "2px",
                                overflow: "hidden"
                            }}>
                                <div style={{
                                    height: "100%",
                                    width: `${progressPercent}%`,
                                    backgroundColor: progressPercent > 50 ? "#4CAF50" : progressPercent > 10 ? "#FFC107" : "#F44336",
                                    transition: "width 1s linear, background-color 1s ease"
                                }}></div>
                            </div>
                            <div style={{
                                display: "flex",
                                justifyContent: "center",
                                fontSize: "0.8em",
                                marginTop: "0.2em",
                                color: "#666"
                            }}>
                                zostało {timeRemaining < 60 ? timeRemaining : "1 min " + (timeRemaining - 60)}s
                            </div>
                        </div>
                    )}

                    {error && <div className="login-pin-error">{error}</div>}
                    {loading && <div className="login-pin-loading">Weryfikacja...</div>}

                    <p className="login-pin-instructions" style={{ marginTop: "1em" }}>
                        {pinEntered ? "Zatwierdź PIN w aplikacji" : "Wprowadź PIN z aplikacji"}
                    </p>

                </div>
            </div>
        );
    }
}

LoginPIN.propTypes = {
    onAuthStatusChange: PropTypes.func.isRequired,
    clearPollingRef: PropTypes.func
};

export default LoginPIN;